import React, { useEffect, useMemo, useState } from "react";
import { Alert, Button, Card, Container } from "react-bootstrap";
import { loadStripe, Stripe } from "@stripe/stripe-js";
import axios from "axios";
import AppConstants from "../../AppConstants";
import useAuthToken from "../../hooks/useAuthToken";

export default function StripeReturnPage() {
  const [status, setStatus] = useState<string>("processing");
  const [message, setMessage] = useState<string>("Finalizing your payment…");
  const [error, setError] = useState<string | null>(null);
  const { userToken } = useAuthToken();

  const stripePromise = useMemo(
    () => loadStripe(AppConstants.STRIPE_PUBLISHABLE_KEY || ""),
    []
  );

  useEffect(() => {
    const finalize = async () => {
      try {
        const params = new URLSearchParams(window.location.search);
        const clientSecret = params.get("payment_intent_client_secret");
        const setupClientSecret = params.get("setup_intent_client_secret");
        const redirectStatus = params.get("redirect_status");
        const flow = params.get("flow");

        if (!clientSecret && !setupClientSecret) {
          setError("Missing client secret from Stripe return.");
          setStatus("error");
          return;
        }

        const stripe: Stripe | null = await stripePromise;
        if (!stripe) {
          setError("Stripe failed to initialize.");
          setStatus("error");
          return;
        }

        if (setupClientSecret) {
          // Handle SetupIntent (add payment method)
          const { setupIntent, error: siErr } = await stripe.retrieveSetupIntent(
            setupClientSecret
          );
          if (siErr) {
            setError(siErr.message || "Failed to retrieve setup intent.");
            setStatus("error");
            return;
          }
          const siStatus = setupIntent?.status || redirectStatus || "unknown";
          setStatus(siStatus);
          if (siStatus === "succeeded") {
            // Optionally persist the PM via backend (in case redirect happened)
            const pmId = (setupIntent?.payment_method as string) || "";
            const siId = setupIntent?.id;
            if (userToken && pmId && siId) {
              try {
                await axios.post(
                  AppConstants.PAYMENTS_ADD_PAYMENT_METHOD_API,
                  { paymentMethodId: pmId, setupIntentId: siId },
                  { headers: { Authorization: `Bearer ${userToken}` } }
                );
                try {
                  await axios.post(
                    AppConstants.PAYMENTS_SET_DEFAULT_METHOD_API,
                    { paymentMethodId: pmId },
                    { headers: { Authorization: `Bearer ${userToken}` } }
                  );
                } catch {}
              } catch {}
            }
            setMessage("Card saved. Redirecting to app…");
            setTimeout(() => {
              window.location.replace("pretrip://payment-method-success");
            }, 1000);
          } else if (
            siStatus === "processing" ||
            siStatus === "requires_action" ||
            siStatus === "requires_confirmation"
          ) {
            setMessage("Verification is processing. You can close this page.");
          } else if (siStatus === "requires_payment_method") {
            setError("Card verification failed. Please try another card.");
          } else {
            setError(`Setup status: ${siStatus}.`);
          }
        } else if (clientSecret) {
          // Handle PaymentIntent (subscription)
          const { paymentIntent, error } = await stripe.retrievePaymentIntent(
            clientSecret
          );
          if (error) {
            setError(error.message || "Failed to retrieve payment status.");
            setStatus("error");
            return;
          }
          const piStatus = paymentIntent?.status || redirectStatus || "unknown";
          setStatus(piStatus);
          if (piStatus === "succeeded") {
            setMessage("Payment confirmed. Redirecting to app…");
            setTimeout(() => {
              window.location.replace("pretrip://subscription-success");
            }, 1200);
          } else if (
            piStatus === "processing" ||
            piStatus === "requires_action" ||
            piStatus === "requires_confirmation"
          ) {
            setMessage("Payment is processing. You can close this page.");
          } else if (piStatus === "requires_payment_method") {
            setError("Payment failed. Please try another card.");
          } else {
            setError(`Payment status: ${piStatus}.`);
          }
        }
      } catch (e: any) {
        setError(e?.message || "Unexpected error while finalizing payment.");
        setStatus("error");
      }
    };
    finalize();
  }, [stripePromise]);

  return (
    <section className="py-5">
      <Container style={{ maxWidth: 640 }}>
        <Card className="shadow-sm" style={{ borderRadius: 16 }}>
          <Card.Body className="p-4">
            <h2 className="mb-3">Payment Return</h2>
            {error ? (
              <Alert variant="danger" className="mb-3">
                {error}
              </Alert>
            ) : (
              <Alert variant="info" className="mb-3">
                {message}
              </Alert>
            )}
            <div className="d-flex gap-2">
              <Button
                variant="primary"
                onClick={() => {
                  if (status === "succeeded") {
                    const search = new URLSearchParams(window.location.search);
                    const flow = search.get("flow");
                    const deep = flow === "pm" ? "pretrip://payment-method-success" : "pretrip://subscription-success";
                    window.location.replace(deep);
                  }
                }}
              >
                Open App
              </Button>
              <Button
                variant="outline-secondary"
                onClick={() => (window.location.href = "/")}
              >
                Go Home
              </Button>
            </div>
          </Card.Body>
        </Card>
      </Container>
    </section>
  );
}
